import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { adminGetBookings, getConfig, adminListSlotBlocks, adminCreateSlotBlock, adminDeleteSlotBlock } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Table as TableIcon, Calendar as CalendarIcon, Lock, Unlock, Ban } from "lucide-react";
import { parseISODate } from "@/lib/format";

const TOKEN_KEY = "ice_admin_token";
const SESSION_TIMES = ["09:30", "10:45", "12:00", "13:15"];
const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAY_HEAD = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const STATUS_SWATCH = {
  submitted: "bg-slate-200 border-slate-900",
  "in-progress": "bg-amber-200 border-amber-900",
  confirmed: "bg-sky-500 border-slate-900",
  "invoice sent": "bg-indigo-200 border-indigo-900",
  paid: "bg-emerald-500 border-slate-900",
};

const STATUS_STYLES = {
  submitted: "bg-slate-100 text-slate-900 border-slate-900",
  "in-progress": "bg-amber-100 text-amber-900 border-amber-900",
  confirmed: "bg-sky-500 text-white border-slate-900",
  "invoice sent": "bg-indigo-100 text-indigo-900 border-indigo-900",
  paid: "bg-emerald-500 text-white border-slate-900",
};

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Build 6x7 grid of Date objects for the visible month (Mon-first)
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const dayOfWeek = (first.getDay() + 6) % 7; // Mon=0
  const start = new Date(year, month, 1 - dayOfWeek);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return cells;
}

export default function AdminCalendarPage() {
  const navigate = useNavigate();
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [bookings, setBookings] = useState([]);
  const [config, setConfig] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Start on Nov 2026 (booking window start)
  const [viewYear, setViewYear] = useState(2026);
  const [viewMonth, setViewMonth] = useState(10); // 0-indexed → Nov

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const [b, c, bl] = await Promise.all([adminGetBookings(token), getConfig(), adminListSlotBlocks(token)]);
        setBookings(b);
        setConfig(c);
        setBlocks(bl);
      } catch (err) {
        toast.error("Failed to load calendar");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const blockedSlots = useMemo(() => {
    const map = {}; // key `${date}|${time}` → block; `${date}|day` for whole-day
    for (const b of blocks) {
      const k = b.time ? `${b.date}|${b.time}` : `${b.date}|day`;
      map[k] = b;
    }
    return map;
  }, [blocks]);

  const isBlocked = (iso, time) => {
    return blockedSlots[`${iso}|day`] || blockedSlots[`${iso}|${time}`] || null;
  };

  const isDayBlocked = (iso) => !!blockedSlots[`${iso}|day`];

  const toggleBlock = async (iso, time) => {
    const existing = blockedSlots[`${iso}|${time}`];
    try {
      if (existing) {
        await adminDeleteSlotBlock(token, existing.id);
        setBlocks((prev) => prev.filter((b) => b.id !== existing.id));
        toast.success("Slot re-opened");
      } else {
        const created = await adminCreateSlotBlock(token, { date: iso, time });
        setBlocks((prev) => [...prev, created]);
        toast.success("Slot closed off");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed";
      toast.error(typeof msg === "string" ? msg : "Failed");
    }
  };

  const toggleDayBlock = async (iso) => {
    const existing = blockedSlots[`${iso}|day`];
    try {
      if (existing) {
        await adminDeleteSlotBlock(token, existing.id);
        setBlocks((prev) => prev.filter((b) => b.id !== existing.id));
        toast.success("Day re-opened");
      } else {
        const created = await adminCreateSlotBlock(token, { date: iso, time: null });
        setBlocks((prev) => [...prev, created]);
        toast.success("Day closed off");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed";
      toast.error(typeof msg === "string" ? msg : "Failed");
    }
  };

  // Map ISO date → array of bookings (index by time). Skip cancelled entirely.
  const bookingsByDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (b.cancelled || b.status === "cancelled") continue;
      if (!map[b.date]) map[b.date] = {};
      map[b.date][b.time] = b;
    }
    return map;
  }, [bookings]);

  const validDates = new Set(config?.valid_dates || []);
  const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  // Stats for the current visible month (excludes cancelled)
  const monthStats = useMemo(() => {
    let booked = 0;
    for (const b of bookings) {
      if (b.cancelled || b.status === "cancelled") continue;
      const d = parseISODate(b.date);
      if (d.getFullYear() === viewYear && d.getMonth() === viewMonth) booked++;
    }
    return { booked };
  }, [bookings, viewYear, viewMonth]);

  const goPrev = () => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };
  const goNext = () => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };
  const goToday = () => {
    const now = new Date();
    const validSet = config?.valid_dates || [];
    const isoNow = toIso(now);
    // Clamp to booking window if today is outside the configured ranges
    if (validSet.length && (isoNow < validSet[0] || isoNow > validSet[validSet.length - 1])) {
      const target = parseISODate(validSet[0]);
      setViewYear(target.getFullYear());
      setViewMonth(target.getMonth());
      return;
    }
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto px-4 md:px-8 py-20 text-center">
          <p className="font-mono-tight text-sm text-slate-600 mb-4">Admin sign-in required.</p>
          <Link to="/admin" className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 inline-block">
            Go to admin login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Toolbar */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-1">
              Calendar view
            </div>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-none" data-testid="calendar-title">
              {MONTHS_LONG[viewMonth]} {viewYear}
            </h1>
            <div className="mt-2 font-mono-tight text-xs text-slate-500">
              Active bookings this month:{" "}
              <span className="text-slate-900 font-bold">{monthStats.booked}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={goPrev}
              className="bg-white text-slate-900 border-2 border-slate-900 brutal-shadow brutal-hover p-2"
              data-testid="cal-prev"
              title="Previous month"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              onClick={goToday}
              className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-3 py-2"
              data-testid="cal-today"
            >
              Today
            </button>
            <button
              onClick={goNext}
              className="bg-white text-slate-900 border-2 border-slate-900 brutal-shadow brutal-hover p-2"
              data-testid="cal-next"
              title="Next month"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <div className="w-3" />
            <Link
              to="/admin"
              className="bg-slate-900 text-white font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-3 py-2 flex items-center gap-2"
              data-testid="cal-view-table"
            >
              <TableIcon className="w-3.5 h-3.5" strokeWidth={2.5} /> Table view
            </Link>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 mb-3 font-mono-tight text-[10px] uppercase tracking-widest">
          {Object.keys(STATUS_SWATCH).map((s) => (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`inline-block w-3 h-3 border ${STATUS_SWATCH[s]}`} />
              <span className="text-slate-600">{s}</span>
            </div>
          ))}
        </div>

        {/* Calendar */}
        <div className="border-2 border-slate-900 bg-white brutal-shadow" data-testid="calendar-grid">
          <div className="grid grid-cols-7 border-b-2 border-slate-900 bg-slate-900 text-white">
            {WEEKDAY_HEAD.map((w) => (
              <div key={w} className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-center py-2">
                {w}
              </div>
            ))}
          </div>

          {loading ? (
            <div className="p-16 text-center font-mono-tight uppercase tracking-widest">Loading…</div>
          ) : (
            <div className="grid grid-cols-7">
              {cells.map((d, i) => {
                const iso = toIso(d);
                const inMonth = d.getMonth() === viewMonth;
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                const isActiveDay = validDates.has(iso);
                const dayBookings = bookingsByDate[iso] || {};

                return (
                  <div
                    key={i}
                    className={`min-h-[140px] border-r-2 border-b-2 border-slate-900 last:border-r-0 p-2 ${
                      !inMonth ? "bg-slate-50 opacity-40" : isDayBlocked(iso) ? "bg-slate-200" : isWeekend ? "bg-slate-50" : "bg-white"
                    }`}
                    data-testid={`cal-cell-${iso}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`font-mono-tight font-bold text-sm ${!inMonth ? "text-slate-400" : "text-slate-900"}`}>
                        {d.getDate()}
                      </div>
                      <div className="flex items-center gap-1">
                        {isActiveDay && !isDayBlocked(iso) && (
                          <div className="font-mono-tight text-[9px] uppercase tracking-widest text-sky-600">
                            Active
                          </div>
                        )}
                        {isActiveDay && (
                          <button
                            type="button"
                            onClick={() => toggleDayBlock(iso)}
                            className={`p-0.5 border ${isDayBlocked(iso) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                            title={isDayBlocked(iso) ? "Re-open whole day" : "Close whole day"}
                            data-testid={`cal-day-block-${iso}`}
                          >
                            {isDayBlocked(iso) ? <Unlock className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                    </div>

                    {isActiveDay ? (
                      isDayBlocked(iso) ? (
                        <div className="border-2 border-slate-900 bg-slate-900 text-white px-2 py-2 font-mono-tight text-[10px] uppercase tracking-widest text-center">
                          Day closed
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {SESSION_TIMES.map((t) => {
                            const b = dayBookings[t];
                            const blk = isBlocked(iso, t);
                            if (blk && !b) {
                              return (
                                <div
                                  key={t}
                                  className="border border-slate-900 bg-slate-800 text-white px-1.5 py-1 font-mono-tight text-[10px] flex items-center justify-between gap-1"
                                  data-testid={`cal-slot-${iso}-${t.replace(":","")}-blocked`}
                                >
                                  <span>{t}</span>
                                  <button
                                    type="button"
                                    onClick={() => toggleBlock(iso, t)}
                                    className="hover:bg-white/10 p-0.5"
                                    title="Re-open slot"
                                  >
                                    <Unlock className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            }
                            if (!b) {
                              return (
                                <div
                                  key={t}
                                  className="border border-dashed border-slate-300 bg-white px-1.5 py-1 text-[10px] text-slate-400 font-mono-tight flex items-center justify-between gap-1"
                                  data-testid={`cal-slot-${iso}-${t.replace(":","")}-empty`}
                                >
                                  <span>{t} · open</span>
                                  <button
                                    type="button"
                                    onClick={() => toggleBlock(iso, t)}
                                    className="hover:bg-slate-100 p-0.5"
                                    title="Close off slot"
                                  >
                                    <Lock className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            }
                            const cls = STATUS_STYLES[b.status || "submitted"] || STATUS_STYLES.submitted;
                            return (
                              <button
                                key={t}
                                type="button"
                                onClick={() => navigate(`/admin?edit=${b.id}`)}
                                className={`w-full text-left border ${cls} px-1.5 py-1 font-mono-tight cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-sky-500`}
                                title={`${b.school_name} · ${b.teacher_name} · ${b.email} — click to edit`}
                                data-testid={`cal-slot-${iso}-${t.replace(":","")}`}
                              >
                                <div className="flex items-center justify-between gap-1 text-[10px] font-bold leading-tight">
                                  <span>{t}</span>
                                  <span className="whitespace-nowrap">×{b.group_size}</span>
                                </div>
                                <div className="text-[10px] leading-tight truncate">{b.school_name}</div>
                                <div className="text-[9px] leading-tight opacity-80 uppercase tracking-wider">{b.status || "submitted"}</div>
                              </button>
                            );
                          })}
                        </div>
                      )
                    ) : (
                      inMonth && !isWeekend && (
                        <div className="font-mono-tight text-[10px] text-slate-400">Outside window</div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
